/**
 * The host side of theme persistence — the client half of the server work in chorestory #495,
 * tracked under chorestory-next #36 (R9.2) and #38 (R9.4).
 *
 * `ThemeController` asks for two functions and nothing else. It supplies the profile id it was
 * bound to, and it refuses any answer that is not a RECEIPT:
 *
 *     readTheme(profileId)        -> { receiptId, status: 'loaded', profileId, mode }
 *     saveTheme(profileId, mode)  -> { receiptId, status: 'saved',  profileId, mode }
 *
 * A missing receipt, a wrong `profileId` or a mode the host did not confirm all land the
 * controller in `error`, and the screen then says it could not save. That is deliberate: a theme
 * that silently fails to save is the defect this whole change exists to remove.
 *
 * **The mode is read back off the RESPONSE, never off the argument.** `PUT /api/auth/profile`
 * returns the stored profile, so `updated.themePreference` is what the host actually holds. Had
 * this echoed the requested mode instead, a host that stored something else — or stored nothing —
 * would still have produced a receipt saying "saved", and the screen would state a fact about the
 * account that is false. The controller's own `matches` check then compares that confirmed mode
 * against what the user picked, so a disagreement surfaces rather than passing.
 *
 * **The identity is checked the same way, and its limit is stated rather than implied.** The
 * response's `id` is compared with the `profileId` this call was made for. That catches a response
 * describing a DIFFERENT member — a stale in-flight answer after a profile switch, a proxy serving
 * another session's cached body — so the receipt can never mis-attribute one member's row to
 * another on screen. It does **not** stop the write itself from landing on the wrong row: the
 * server picks the row from the session cookie (`auth.routes.js:162` reads `req.user.userId`), so
 * only a server-checked expected identity could prevent that. This is an attribution check, and
 * saying so is the point — the earlier version of this file claimed the controller's
 * `r.profileId === selectedId` test would catch a wrong id, which was FALSE while the receipt
 * echoed the argument back and made that comparison a tautology.
 *
 * **The comparison is textual, deliberately.** `users.id` is an integer serial, so the endpoint
 * returns a NUMBER, while `toActor` (`api/session.js:67`) stringifies it before it ever reaches
 * `ProfileThemeBinding`. A strict `!==` would therefore be true on every real save and would turn
 * this feature into a permanent "we couldn't save". `themeStore.test.jsx` pins that case.
 *
 * **Why the receipt id is built here.** The endpoint issues no receipt id of its own. `hasReceipt`
 * needs a non-empty string, so this constructs one that names what was confirmed and when. It is
 * a local trace identifier, not a token from the server, and nothing may treat it as one.
 *
 * **AD-01 boundary, the same one `ThemeController` documents.** A remembered theme is a display
 * preference and carries NO authority. This module reads and writes exactly one column and holds
 * no session, actor or permission state.
 */
import { api } from '../api/client.js';

const PROFILE_PATH = '/api/auth/profile';

/** The three the CHECK constraint on `users.theme_preference` allows, and the controller too. */
export const MODES = Object.freeze(['system', 'light', 'dark']);

function confirmedMode(profile, profileId, operation) {
  // Textual on purpose — see the header. A missing `id` fails too: an answer that will not say
  // whose row it is cannot be attributed to anyone.
  const answeredFor = profile?.id === null || profile?.id === undefined ? '' : String(profile.id);
  if (answeredFor === '' || answeredFor !== String(profileId)) {
    throw new Error(
      `${PROFILE_PATH} answered for a different member on ${operation}: `
      + `asked for ${JSON.stringify(String(profileId))}, got ${JSON.stringify(answeredFor)}`,
    );
  }
  const mode = profile?.themePreference;
  if (!MODES.includes(mode)) {
    throw new Error(
      `${PROFILE_PATH} did not confirm a theme on ${operation}: got ${JSON.stringify(mode)}`,
    );
  }
  return mode;
}

/**
 * @param {object} [client] the API client seam, so a test can drive this without a network.
 */
export function createThemeStore(client = api) {
  return {
    async readTheme(profileId) {
      const profile = await client.get(PROFILE_PATH);
      const mode = confirmedMode(profile, profileId, 'read');
      return { receiptId: `theme:read:${profileId}:${mode}:${Date.now()}`, status: 'loaded', profileId, mode };
    },

    async saveTheme(profileId, mode) {
      if (!MODES.includes(mode)) throw new Error(`unknown theme mode: ${String(mode)}`);
      const updated = await client.put(PROFILE_PATH, { themePreference: mode });
      const stored = confirmedMode(updated, profileId, 'save');
      return { receiptId: `theme:save:${profileId}:${stored}:${Date.now()}`, status: 'saved', profileId, mode: stored };
    },
  };
}

/** The one the application uses. A test builds its own with `createThemeStore`. */
export const themeStore = createThemeStore();
